import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { StatusBadge } from "@/components/shared/status-badge";
import { AIScopePreview } from "@/components/intake/ai-scope-preview";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertTriangle,
  Download,
  ExternalLink,
  FileText,
  User,
  Video,
} from "lucide-react";
import { formatDateTime } from "@/lib/format";
import { parseWorkRequestAttachment } from "@/lib/work-request-attachments";
import type { WorkRequest } from "@db/schema";
import type { AIScopingResponse } from "@/lib/intake-schemas";
import { getWorkRequestCategoryLabel } from "@shared/work-request";
import type { StoredWorkRequestAttachment } from "@shared/work-request";

interface WorkRequestDetailContentProps {
  workRequest: WorkRequest;
  statusActions?: ReactNode;
  onOpenTicket?: () => void;
}

function renderRequestDetails(details: unknown) {
  if (!details || typeof details !== "object") return null;
  const entries = Object.entries(details as Record<string, unknown>);
  if (entries.length === 0) return null;

  return (
    <dl className="space-y-2">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt className="text-sm font-medium text-muted-foreground capitalize">
            {key.replace(/([A-Z])/g, " $1").replace(/_/g, " ")}
          </dt>
          <dd className="mt-0.5 text-sm">
            {typeof value === "string" ? value : JSON.stringify(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function WorkRequestAttachmentCard({
  workRequestId,
  attachment,
  index,
}: {
  workRequestId: string;
  attachment: StoredWorkRequestAttachment | string;
  index: number;
}) {
  const parsed = parseWorkRequestAttachment(attachment, index);
  const { data: preview } = useQuery<{ downloadUrl: string }>({
    queryKey: [
      `/api/intake/work-requests/${workRequestId}/attachments/${parsed.attachmentId}/download-url`,
      "inline",
    ],
    queryFn: async () => {
      const response = await fetch(
        `/api/intake/work-requests/${workRequestId}/attachments/${parsed.attachmentId}/download-url?disposition=inline`,
        { credentials: "include" },
      );
      if (!response.ok) {
        throw new Error("Could not load attachment preview");
      }
      return response.json();
    },
    enabled: Boolean(parsed.isR2 && parsed.attachmentId),
  });
  const { data: download } = useQuery<{ downloadUrl: string }>({
    queryKey: [
      `/api/intake/work-requests/${workRequestId}/attachments/${parsed.attachmentId}/download-url`,
      "attachment",
    ],
    queryFn: async () => {
      const response = await fetch(
        `/api/intake/work-requests/${workRequestId}/attachments/${parsed.attachmentId}/download-url?disposition=attachment`,
        { credentials: "include" },
      );
      if (!response.ok) {
        throw new Error("Could not load attachment download");
      }
      return response.json();
    },
    enabled: Boolean(parsed.isR2 && parsed.attachmentId),
  });

  const previewHref =
    parsed.kind === "image" ? (preview?.downloadUrl ?? parsed.href) : parsed.href;
  const viewHref = preview?.downloadUrl ?? parsed.href ?? download?.downloadUrl;
  const downloadHref = download?.downloadUrl ?? parsed.href;
  const Icon = parsed.kind === "video" ? Video : FileText;

  return (
    <li className="rounded-lg border border-border bg-card p-3 space-y-3">
      {parsed.kind === "image" && previewHref ? (
        <a
          href={previewHref}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <img
            src={previewHref}
            alt={parsed.label}
            className="max-h-48 w-full rounded-md border border-border object-contain bg-muted/30"
          />
        </a>
      ) : (
        <div className="flex items-center gap-2 text-sm font-medium">
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
          <span>{parsed.label}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {viewHref && (
          <Button variant="outline" size="sm" asChild>
            <a href={viewHref} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              View
            </a>
          </Button>
        )}
        {downloadHref && (
          <Button variant="outline" size="sm" asChild>
            <a href={downloadHref} download={parsed.downloadName}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Download
            </a>
          </Button>
        )}
        {!viewHref && !downloadHref && (
          <p className="text-xs text-muted-foreground">
            Attachment data is unavailable or unsupported.
          </p>
        )}
      </div>
    </li>
  );
}

export function WorkRequestDetailContent({
  workRequest,
  statusActions,
  onOpenTicket,
}: WorkRequestDetailContentProps) {
  const aiScoping: AIScopingResponse = {
    category: workRequest.category,
    scope_summary: workRequest.aiScopeSummary ? [workRequest.aiScopeSummary] : [],
    assumptions: workRequest.aiAssumptions ?? [],
    missing_info: workRequest.aiMissingInfo ?? [],
    estimate_hours_number: workRequest.aiEstimateHours ?? 0,
    estimate_bucket:
      (workRequest.aiEstimateBucket as "small" | "medium" | "large") ?? "medium",
    confidence_score: workRequest.aiConfidenceScore ?? 0,
    recommended_next_steps: workRequest.aiRecommendedNextSteps ?? "",
  };

  const hasAiScoping =
    workRequest.aiScopeSummary ||
    (workRequest.aiAssumptions && workRequest.aiAssumptions.length > 0) ||
    workRequest.aiEstimateHours != null;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <User className="h-4 w-4" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Full Name
                </dt>
                <dd className="mt-0.5 text-sm">{workRequest.fullName}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Email
                </dt>
                <dd className="mt-0.5 text-sm">{workRequest.email}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Company
                </dt>
                <dd className="mt-0.5 text-sm">{workRequest.company}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Phone
                </dt>
                <dd className="mt-0.5 text-sm">{workRequest.phone || "\u2014"}</dd>
              </div>
              {workRequest.websiteUrl && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-muted-foreground">
                    Website
                  </dt>
                  <dd className="mt-0.5 text-sm">
                    <a
                      href={workRequest.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {workRequest.websiteUrl}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Request Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Category
              </dt>
              <dd className="mt-0.5 text-sm">
                {getWorkRequestCategoryLabel(workRequest.category)}
              </dd>
            </div>

            {renderRequestDetails(workRequest.requestDetails)}

            {workRequest.loomUrl && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Loom Recording
                </dt>
                <dd className="mt-0.5 text-sm">
                  <a
                    href={workRequest.loomUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {workRequest.loomUrl}
                  </a>
                </dd>
              </div>
            )}

            {workRequest.attachments && workRequest.attachments.length > 0 && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Attachments
                </dt>
                <dd className="mt-2">
                  <ul className="grid gap-3 sm:grid-cols-2">
                    {workRequest.attachments.map((attachment, index) => (
                      <WorkRequestAttachmentCard
                        key={`${workRequest.id}-${index}`}
                        workRequestId={workRequest.id}
                        attachment={attachment}
                        index={index}
                      />
                    ))}
                  </ul>
                </dd>
              </div>
            )}
          </CardContent>
        </Card>

        {hasAiScoping && (
          <div>
            <h3 className="text-base font-heading font-semibold mb-3">
              AI Scope Analysis
            </h3>
            <AIScopePreview
              aiScoping={aiScoping}
              isCovered={workRequest.productiveCoverage === "covered"}
            />
          </div>
        )}
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading">Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <dt className="text-sm font-medium text-muted-foreground">
                Current Status
              </dt>
              <dd>
                <StatusBadge type="workRequest" status={workRequest.status} />
              </dd>
            </div>

            {workRequest.isUrgent && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-red-700 dark:text-red-400" />
                <span className="text-sm font-medium text-red-700 dark:text-red-400">
                  Marked as Urgent
                </span>
              </div>
            )}

            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Customer Status
              </dt>
              <dd className="mt-0.5 text-sm">{workRequest.customerStatus}</dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Productive coverage
              </dt>
              <dd className="mt-0.5 text-sm">
                {workRequest.productiveCoverage === "covered"
                  ? `Covered by fixed-price service${
                      workRequest.productiveBudgetId
                        ? ` (budget ${workRequest.productiveBudgetId})`
                        : ""
                    }`
                  : workRequest.productiveCoverage === "no_active_service"
                    ? workRequest.productiveServiceId
                      ? "Client linked; not covered (T&M or out of scope)"
                      : "Client linked; no active Productive service"
                    : "Unmatched — flagged for review"}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Submitted
              </dt>
              <dd className="mt-0.5 text-sm">
                {formatDateTime(workRequest.createdAt)}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Last Updated
              </dt>
              <dd className="mt-0.5 text-sm">
                {formatDateTime(workRequest.updatedAt)}
              </dd>
            </div>

            {workRequest.ticketId && onOpenTicket && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Linked Ticket
                </dt>
                <dd className="mt-0.5 text-sm">
                  <Button
                    variant="link"
                    className="p-0 h-auto"
                    onClick={onOpenTicket}
                  >
                    View Ticket
                  </Button>
                </dd>
              </div>
            )}

            {workRequest.aiConfidenceScore != null && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  AI Confidence
                </dt>
                <dd className="mt-0.5 text-sm">
                  {Math.round(workRequest.aiConfidenceScore * 100)}%
                </dd>
              </div>
            )}

            {statusActions}
          </CardContent>
        </Card>

        {(workRequest.userProvidedMissingInfo || workRequest.userAssumptionContext) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-heading">
                Customer Refinement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {workRequest.userProvidedMissingInfo && (
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Additional Info Provided
                  </dt>
                  <dd className="mt-0.5 text-sm">
                    {workRequest.userProvidedMissingInfo}
                  </dd>
                </div>
              )}
              {workRequest.userAssumptionContext && (
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Assumption Clarifications
                  </dt>
                  <dd className="mt-0.5 text-sm">
                    {workRequest.userAssumptionContext}
                  </dd>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
